import { useLiveQuery } from 'dexie-react-hooks'
import type { Category, Transaction } from '../db/database'
import { envelopeHistory, type Envelope, type EnvelopeDay } from '../db/budget'
import { formatAriary, formatSigned } from '../lib/money'
import { formatShortDate, today, weekdayName } from '../lib/dates'
import type { CycleRange } from '../lib/cycles'
import { Icon } from './Icon'
import './EnvelopeHistory.css'

/**
 * Où est passé l'argent d'une enveloppe.
 *
 * La question se pose de la même façon quel que soit le mode de dépense : « jour
 * par jour », « en une fois » ou « au fil de l'eau ». Réserver l'historique aux
 * enveloppes planifiées laissait les deux autres sans réponse — or ce sont
 * justement celles dont rien ne raconte le détail.
 *
 * Une journée porte souvent PLUSIEURS lignes : celle qu'on avait prévue, plus
 * celles ajoutées au fil du marché. N'afficher que le total du jour cachait les
 * secondes, et le compte de l'enveloppe devenait impossible à refaire.
 */
export function EnvelopeDays(
  { days, category, onOpen }: {
    days: EnvelopeDay[]
    category: Category
    /** Absent = la liste reste en lecture seule, ce qui est le repli sûr. */
    onOpen?: (transaction: Transaction) => void
  },
) {
  return (
    <ul className="spent-days">
      {days.map((jour) => (
        <li key={jour.date}>
          <div className="spent-head">
            <span className="spent-date">
              <b>{jour.date === today() ? "Aujourd'hui" : weekdayName(jour.date)}</b>{' '}
              {formatShortDate(jour.date)}
            </span>
            {/* Le NET du jour, et son sens dit en toutes lettres : un retrait
                d'épargne rend de l'argent, l'annoncer « dépensé » serait faux. */}
            <span className={`spent-total tabular ${jour.net < 0 ? 'is-back' : ''}`}>
              {formatAriary(Math.abs(jour.net))} · {jour.net < 0 ? 'repris' : 'dépensé'}
            </span>
          </div>

          <ul className="spent-rows">
            {jour.transactions.map((transaction) => (
              <li key={transaction.id}>
                <button type="button" disabled={!onOpen}
                        onClick={() => onOpen?.(transaction)}>
                  <span className="spent-name">
                    {transaction.title ?? category.name}
                  </span>
                  {jour.offPlan.has(transaction.id) && transaction.direction === 'expense' && (
                    <span className="spent-tag">hors plan</span>
                  )}
                  <span className={`spent-amount tabular ${
                    transaction.direction === 'income' ? 'is-income' : ''}`}>
                    {formatSigned(transaction.amount, transaction.direction)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  )
}

/**
 * L'historique d'une enveloppe, en feuille.
 *
 * Volontairement en LECTURE seule : corriger passe par le détail d'une ligne,
 * comme partout ailleurs. Un écran qui répond à « où est passé mon argent ? »
 * n'a pas à être aussi celui qui le modifie.
 */
export function EnvelopeHistorySheet(
  { walletId, category, envelope, range, onClose, onOpen }: {
    walletId: string
    category: Category
    envelope: Envelope
    range: CycleRange
    onClose: () => void
    onOpen?: (transaction: Transaction) => void
  },
) {
  const days = useLiveQuery(
    () => envelopeHistory(walletId, category.id, range),
    [walletId, category.id, range], undefined,
  )

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true"
         aria-label={`Opérations de ${category.name}`}>
      <div className="sheet history-sheet">
        <header className="sheet-head">
          <h2>Opérations · {category.name}</h2>
          <button type="button" className="sheet-close" onClick={onClose} aria-label="Fermer">
            <Icon name="close" size={20} />
          </button>
        </header>

        <div className="history-body">
          {/* Les deux nombres de l'enveloppe, rappelés ici : on vient chercher
              d'où vient un écart, pas la liste pour elle-même. */}
          <p className="history-recap">
            {envelope.carries_over
              ? <>
                  <strong className="tabular">{formatAriary(envelope.spent)}</strong>
                  {' '}mis de côté sur{' '}
                  <strong className="tabular">{formatAriary(envelope.accumulated)}</strong>
                </>
              : <>
                  <strong className="tabular">{formatAriary(envelope.spent)}</strong>
                  {' '}dépensés sur{' '}
                  <strong className="tabular">{formatAriary(envelope.amount)}</strong>
                  {envelope.remaining >= 0
                    ? <>{' · '}<strong className="tabular">
                        {formatAriary(envelope.remaining)}</strong> restants</>
                    : <>{' · dépassé de '}<strong className="tabular">
                        {formatAriary(-envelope.remaining)}</strong></>}
                </>}
          </p>

          {days === undefined ? null : days.length === 0 ? (
            <p className="history-empty">
              Rien n'a encore bougé dans cette enveloppe sur cette période.
            </p>
          ) : (
            <EnvelopeDays days={days} category={category} onOpen={onOpen} />
          )}
        </div>
      </div>
    </div>
  )
}
